import React from "react";
import { Fragment, useEffect, useState } from "react";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { LayersControl, MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import { icon } from "leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { HeatmapLayer } from "react-leaflet-heatmap-layer-v3";
import Papa from "papaparse";

import { SiteHeader, HowToUse, ProjectInfo } from "./Info";

import "leaflet/dist/leaflet.css";

import IMAGE_DATA_FILE from "./data/image_data.csv";
import HEATMAP_DATA_FILE from "./data/heatmap_data.csv";

const INIT_ZOOM = 17;
const MAX_ZOOM = 20;
// const INIT_ZOOM = 17;
// const MAX_ZOOM = 18;

const SLOPE_ID = 0;
const STAIRS_ID = 1;
const STEP_ID = 2;
const OTHER_ID = 99;

const ICON_URL = "./images/marker.png";


function readCsv(csvFile, handleData) {
  Papa.parse(csvFile, {
    download: true,
    dynamicTyping: true,
    skipEmptyLines: true,
    complete: function(result) {
      handleData(result.data);
    }
  });
}


function EachImage({src}) {
  function handleClick(img) {
    window.open(img, "_blank");
  }

  return (
    <img
      className="popupImage"
      src={src}
      onClick={() => handleClick(src)} />
  );
}


function ImageMarker({center, imageData}) {
  const myIcon = icon({
    iconUrl: ICON_URL,
    iconSize: [32, 32],
  });

  return (
    <Fragment>
      <MarkerClusterGroup
        chunkedLoading={true} >
        {imageData.map((eachImageData, i) => (
          <Marker
            key={i}
            position={[eachImageData[0], eachImageData[1]]}
            icon={myIcon}>
            <Popup>
              <div className="popupDiv">
                <EachImage src={eachImageData[2]} />
              </div>
            </Popup>
          </Marker>
        ))}
      </MarkerClusterGroup>
    </Fragment>
  );
}


function Heatmap({heatmapData}) {
  const heatmapOptions = {
    max: 100,
    useLocalExtrema: false,
    fitBoundsOnLoad: false,
    fitBoundsOnUpdate: false,
    radius: 20,
    blur: 20,
    opacity: 0.5
  };
  return (
    <Fragment>
      <HeatmapLayer
        points={heatmapData}
        longitudeExtractor={(point) => point[1]}
        latitudeExtractor={(point) => point[0]}
        intensityExtractor={(point) => parseFloat(point[2])}
        {...heatmapOptions}
      />
    </Fragment>
  );
}


function Tile() {
  return (
    <Fragment>
      <TileLayer
        maxNativeZoom={INIT_ZOOM}
        maxZoom={MAX_ZOOM}
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png"
      />
    </Fragment>
  );
}


function Map({center, stepImageData, stairsImageData, slopeImageData, otherImageData, heatmapData}) {

  return (
    <Fragment>
      <MapContainer 
        className="mapContainer"
        center={center}
        zoom={INIT_ZOOM}
        maxZoom={MAX_ZOOM}
        scrollWheelZoom={true}>

        <LayersControl position="topright">
          {/* Image marker layer. */}
          <LayersControl.Overlay checked name="Step photo">
            <ImageMarker center={center} imageData={stepImageData} />
          </LayersControl.Overlay>
          <LayersControl.Overlay checked name="Stairs photo">
            <ImageMarker center={center} imageData={stairsImageData} />
          </LayersControl.Overlay>
          <LayersControl.Overlay checked name="Slope photo">
            <ImageMarker center={center} imageData={slopeImageData} />
          </LayersControl.Overlay>
          <LayersControl.Overlay checked name="Other photo">
            <ImageMarker center={center} imageData={otherImageData} />
          </LayersControl.Overlay>

          {/* Heatmap layer. */}
          <LayersControl.Overlay checked name="Heatmap">
            <Heatmap 
              heatmapData={heatmapData} />
          </LayersControl.Overlay>
        </LayersControl>

        <Tile />

      </MapContainer>
    </Fragment>
  );
}


export default function Container() {
  const center = [35.662, 139.634];
  // const center = [35.863, 139.607];
  const [stepImageData, setStepImageData] = useState([]);
  const [stairsImageData, setStairsImageData] = useState([]);
  const [slopeImageData, setSlopeImageData] = useState([]);
  const [otherImageData, setOtherImageData] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);

  function handleImageData(imageData) {
    const stepImageData = [];
    const stairsImageData = [];
    const slopeImageData = [];
    const otherImageData = [];
    imageData.forEach((eachImgData) => {
      if(eachImgData[3] === STEP_ID) {
        stepImageData.push(eachImgData);
      } else if(eachImgData[3] === STAIRS_ID) {
        stairsImageData.push(eachImgData);
      } else if(eachImgData[3] === SLOPE_ID) {
        slopeImageData.push(eachImgData);
      } else if(eachImgData[3] === OTHER_ID) {
        otherImageData.push(eachImgData);
      }
    });
    setStepImageData(stepImageData);
    setStairsImageData(stairsImageData);
    setSlopeImageData(slopeImageData);
    setOtherImageData(otherImageData);
  }

  function handleHeatmapData(heatmapData) {
    setHeatmapData(heatmapData);
  }

  useEffect(() => {
    readCsv(IMAGE_DATA_FILE, handleImageData);
    readCsv(HEATMAP_DATA_FILE, handleHeatmapData);
  }, []);

  return (
    <Fragment>
      <SiteHeader />
      <Tabs>
        <TabList>
          <Tab>Map</Tab>
          <Tab>How to use</Tab>
          <Tab>Project info</Tab>
        </TabList>
        <TabPanel>
          <Map
            center={center} 
            stepImageData={stepImageData}
            stairsImageData={stairsImageData}
            slopeImageData={slopeImageData}
            otherImageData={otherImageData}
            heatmapData={heatmapData}
          />
        </TabPanel>
        <TabPanel>
          <HowToUse />
        </TabPanel>
        <TabPanel>
          <ProjectInfo />
        </TabPanel>
      </Tabs>
    </Fragment>
  );
}
